import { ref, defineComponent, reactive, watch, computed, toRefs, nextTick } from 'vue';
import useConfigInject from '../../_util/hooks/useConfigInject';
import Icon from '../../icon';
import ResizeObserver from '../../vc-resize-observer';
import { SeriesType } from './const';
import classNames from '../../_util/classNames';
import { getColors } from '../../_util/chartsColors';
export const LegendProps = () => ({
    // 图例数据
    legendList: {
        type: Array,
        default: () => [],
    },
    // 大于1行时，是否显示左右箭头
    isShowTotal: {
        type: Boolean,
        default: false,
    },
    // 是否显示手势，可点击的图例需要置为true
    selectedMode: {
        type: Boolean,
        default: false,
    },
    // 是否需要截断，不要的字符放在[]里；例：有的银行名称一致，需要加（卡号）区分，显示时需要截断
    isLabelRemoveId: {
        type: Boolean,
        default: false,
    },
    // 颜色队列
    colors: {
        type: Array,
        default: [],
    },
    // 是柱状图还是折线图，银行图例的形状 'line | bar | circle'
    scene: {
        type: [String, Array],
        default: 'bar',
    },
    /**  边距
     * in 图例与文字间的间距
     * out 图例与图例间的间距
     * */
    spacing: {
        type: Object,
        default: () => {
            return {
                in: 10,
                out: 24,
            };
        },
    },
    onLegendChange: {
        type: Function,
    },
});
const ChartLegend = defineComponent({
    name: 'WdChartLegend',
    props: LegendProps(),
    setup(props, { emit, slots }) {
        const { prefixCls, configProvider } = useConfigInject('chart-legend', props);
        const legendRef = ref();
        let PageType;
        (function (PageType) {
            PageType[PageType["PREV"] = 0] = "PREV";
            PageType[PageType["NEXT"] = 1] = "NEXT";
        })(PageType || (PageType = {}));
        const data = reactive({
            cur: 1,
            total: 1,
        });
        const { cur, total } = toRefs(data);
        // 点击图例
        const handleClick = (t, index) => {
            emit('legendChange', t, index);
        };
        // 计算y方向移动距离
        const translateTop = computed(() => {
            return !cur.value ? 0 : -((cur.value - 1) * 18);
        });
        // 截取图例多余文字
        const showName = (value) => {
            const findIndexs = value?.indexOf('[');
            if (findIndexs > -1) {
                const seriesName = value.substring(0, findIndexs);
                return seriesName;
            }
            return value;
        };
        const legendListRef = ref();
        const computedWidth = () => {
            if (!legendListRef.value)
                return;
            const listHeight = legendListRef.value;
            const parentHeight = legendRef.value;
            total.value = Math.floor(listHeight.clientHeight / parentHeight.clientHeight);
            cur.value = cur.value > total.value ? (total.value > 0 ? total.value : cur.value) : cur.value;
        };
        // 翻页
        const btnClick = (t) => {
            if (t === PageType.PREV) {
                cur.value = cur.value > 1 ? cur.value - 1 : cur.value;
                return;
            }
            cur.value = cur.value >= total.value ? total.value : cur.value + 1;
        };
        watch(() => props.legendList, () => {
            nextTick(() => computedWidth());
        });
        return () => {
            const list = (props.legendList || []);
            const leftIconClass = classNames({
                [prefixCls.value + '__icon-top']: true,
                [prefixCls.value + '__disabled']: cur.value === 1,
            });
            const rightIconClass = classNames({
                [prefixCls.value + '__icon-bottom']: true,
                [prefixCls.value + '__disabled']: cur.value === total.value,
            });
            let PAGEDOM;
            // 是否显示翻页
            if (props.isShowTotal) {
                PAGEDOM = (<div v-show={total.value > 1} class={`${prefixCls.value}__more-btns`}>
            <Icon class={leftIconClass} onClick={() => btnClick(PageType.PREV)} icon-class="wd-icon-direction_caret-up"/>
            <span>
              {cur.value}/{total.value}
            </span>
            <Icon class={rightIconClass} onClick={() => btnClick(PageType.NEXT)} icon-class="wd-icon-direction_caret-down"/>
          </div>);
            }
            const boxClassName = classNames({
                [prefixCls.value + '__list']: true,
                [prefixCls.value + '__has-more']: total.value > 1,
            });
            return (<div ref={legendRef} class={`${prefixCls.value}`}>
          <ResizeObserver onResize={computedWidth}>
            <div class={boxClassName} ref={legendListRef} style={` transform: translateY(${translateTop.value}px) `}>
              {list.map((item, index) => {
                    const itemClass = classNames({
                        [prefixCls.value + '__item']: true,
                        [prefixCls.value + '__no-selected']: !item?.isShow && item.hasOwnProperty('isShow'),
                    });
                    const itemStyle = {
                        cursor: props.selectedMode ? 'pointer' : 'default',
                        margin: '0 ' + props.spacing.out / 2 + 'px',
                    };
                    const symbolClass = classNames({
                        [prefixCls.value + '__item-symbol']: true,
                        [prefixCls.value +
                            '__symbol-' +
                            (typeof props.scene === 'string' ? props.scene : props.scene[index])]: true,
                    });
                    const colors = getColors(configProvider?.mode);
                    const nColor = props.colors.length > 0 ? props.colors : colors.COMMONCOLOR;
                    const symbolStyle = {
                        backgroundColor: nColor[index % nColor.length] || '',
                        marginRight: ((props.scene === 'string' ? props.scene : props.scene[index]) ===
                            SeriesType.LINE
                            ? props.spacing.in - 1
                            : props.spacing.in) + 'px',
                    };
                    return (<span class={itemClass} onClick={() => handleClick(item, index)} key={item?.id || index} style={itemStyle}>
                    <span class={symbolClass} style={symbolStyle}></span>
                    <span class={`${prefixCls.value}__item-text`}>
                      {props.isLabelRemoveId ? showName(item?.name || item) : item?.name || item}
                    </span>
                    {slots.mark?.(item)}
                  </span>);
                })}
            </div>
          </ResizeObserver>
          {PAGEDOM}
        </div>);
        };
    },
});
export default ChartLegend;
